import { i18n } from "i18next-ko";
import moment from "moment";
import { injectable } from "tsyringe";
import { BadRequestError } from "../../../../tracejs/src/application/BadRequestError";
import { h } from "../../../../tracejs/src/utils/JSXFactory";
import { Tracedo } from "../../../entities/Tracedo";
import { ImportPreset } from "../../../entities/ImportPreset";
import { BaseDialogViewModel } from "../../common/BaseDialogViewModel";
import { TracedoGroupSelectLite } from "../TracedoGroupSelectLite";
import { TracedoGroup } from "../../../entities/TracedoGroup";

/**
 * Import transports
 */
@injectable()
export class Import extends BaseDialogViewModel<any>
{
    /** Selected import preset */
    private presetId: KnockoutObservable<number|null> = ko.observable(null);

    /** File data */
    private file: KnockoutObservable<File|null> = ko.observable(null);
    private fileData: KnockoutObservable<string|null> = ko.observable(null);

    /** Presets collection */
    private presets: KnockoutObservableArray<ImportPreset> = ko.observableArray([]);

	/** Upload element */
	private uploadElement: JQuery;

	/** Is Importing flag */
	private importing: KnockoutObservable<boolean> = ko.observable(false);

    private groupSelectVm?: TracedoGroupSelectLite = null;

    private group?: TracedoGroup = null;

    private result: KnockoutObservableArray<any> = ko.observableArray([]);

    private resultHasErrors: KnockoutObservable<boolean> = ko.observable(false);

    /**
     * ViewModel Startup
     *
     * @param id Tracedo ID
     */
    public async startup()
    {
        await super.startup();

        if (!this.user.isAllowed('entity.tracedo', 'create')) {
            throw new BadRequestError('Access denied', 403);
        }

        let presets: ImportPreset[] = await this.rpc.call('import.getPresets', { entity: 'tracedo', query: { select: "id, name" } });
        this.presets.removeAll();
        presets.forEach(preset => {
            this.presets.push(preset);
        });
    }

	/**
	 * render
	 */
	public async rendered(): Promise<any>
	{
		this.uploadElement = this.element.find('input[type=file]');
		this.uploadElement.kendoUpload({
			async: { saveUrl: "#", removeUrl: "#" },
			multiple: false,
			showFileList: false,
            dropZone: ".dropZoneElement",
			select: (e: kendo.ui.UploadSelectEvent) => {
                e.preventDefault();
                if(e.files.length > 0) {
                    this.fileToBase64(e.files[0])
                        .then(base64 => {
                            this.fileData(base64);
                            this.file(e.files[0]);
                        })
                        .catch(error => {
                            kendo.alert(i18n.t('common.captions.fileReadError') + ': ' + error);
                        });
                }
			}
		} as any);

        this.groupSelectVm = await this.loadViewFrame<TracedoGroupSelectLite>(TracedoGroupSelectLite, 'group-select', {
            caption: i18n.t('common.captions.selectGroupForImport')
        });
        this.groupSelectVm.onSelect.attach(group => {
            this.group = group ?? null;
        });
	}    

    private fileToBase64(file: any): Promise<string>
    {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file.rawFile);
            reader.onload = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = error => reject(error);
        });
    }

    public removeFile(): void
    {
        this.file(null);
        this.fileData(null);
    }

    /**
     * Import
     */
    public async import(): Promise<boolean>
    {
        const presetId = this.presetId();
        const fileData = this.fileData();
        const group = this.group;

        if(!presetId) {
            kendo.alert(i18n.t('common.captions.selectImportPreset'));
            return false;
        }
        
        if(!fileData) {
            kendo.alert(i18n.t('common.captions.selectImportFile'));
            return false;
        }

        this.importing(true);

        const result: { hasErrors: boolean, result: { data: any, errors: string[] }[]} = await this.rpc.call('import.run', { 
            presetId: presetId,
            file: fileData,
            params: {
                groupId: group ? group.id : null
            },
            dryRun: false
        });
        
        // set result
        this.resultHasErrors(result.hasErrors);
        this.result.removeAll();
        result.result.forEach(res => {
            this.result.push(res);
        });
        
        this.importing(false);

        return !result.hasErrors;
    }

	public dialogTemplate = (): HTMLElement => (

        <div>

            <ko if="$root.importing()">
                <div class="dialog-loader"></div>
            </ko>

            <ko ifnot="$root.presets().length > 0">
                <p class="text-center p-4">
                    <big>
                        <em data-bind="i18n: 'common.captions.importNoPresetDefined'"></em>
                    </big>
                </p>
            </ko>

            <ko if="$root.presets().length > 0">
                <label class="w-100">
                    <span class="mb-1 d-inline-block" data-bind="i18n: 'common.captions.importPreset'"></span>
                    <div style="width: calc(100% - 200px);">
                        <select class="form-select mb-2" data-bind="
                            value: $root.presetId,
                            options: $root.presets,
                            optionsCaption: i18n.t('common.captions.selectImportPreset'),
                            optionsText: 'name',
                            optionsValue: 'id'"></select>
                    </div>
                </label>

                <div class="mb-3">
                    <view-frame name="group-select"/>
                </div>

                <div class="mb-2">
                    <label class="w-100">
                        <span class="mb-1 d-inline-block" data-bind="i18n: 'common.captions.importFile'"></span>
                        <div class="dropZoneElement">
                            <input type="file" />
                            <ko if="$root.file()">
                                <div class="upload-file-info p-3">
                                    <big>
                                        <strong data-bind="text: $root.file().name"></strong>
                                        &nbsp; (<span data-bind="filesize: $root.file().size"></span>)
                                        &nbsp; <a href="#" class="btn btn btn-danger upload-file-info-remove" data-bind="click: $root.removeFile.bind($root)"><i class="icon-trash"></i></a>
                                    </big>
                                </div>
                            </ko>
                        </div>
                    </label>
                </div>

                <ko if="$root.result().length > 0">

                    <ko ifnot="$root.resultHasErrors()">
                        <div class="card p-3 overflow-auto k-success-colored">
                            <p class="mb-0">
                                <span data-bind="i18n: 'common.captions.importSuccessful'"></span>
                                <strong data-bind="text: $root.result().length"></strong>
                            </p>
                        </div>
                    </ko>

                    <ko if="$root.resultHasErrors()">
                        <div class="card p-3 overflow-auto k-error-colored">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th class="w-auto" data-bind="i18n: 'common.captions.row'"></th>
                                        <th class="w-50" data-bind="i18n: 'common.captions.errors'"></th>
                                        <th class="w-50" data-bind="i18n: 'common.captions.dataObject'"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <ko foreach="$root.result">
                                        <ko if="$data.errors.length > 0">
                                            <tr>
                                                <td data-bind="text: ($index() + 1)"></td>
                                                <td>
                                                    <ko foreach="$data.errors">
                                                        <p data-bind="text: $data"></p>
                                                    </ko>
                                                </td>
                                                <td>
                                                    <pre data-bind="text: JSON.stringify($data.data, null, 2)"></pre>
                                                </td>
                                            </tr>
                                        </ko>
                                    </ko>
                                </tbody>
                            </table>
                        </div>
                    </ko>

                </ko>
            </ko>

        </div>

	);

}
